import { ReactNode, useState, useEffect, useCallback, useMemo, useRef } from "react";
import ProductTour from "@/components/shared/ProductTour";
import type { TourStep } from "@/components/shared/ProductTour";
import { Navigate, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppSidebar from "./AppSidebar";
import UserAvatar from "@/components/shared/Avatar";
import { useUser } from "@/contexts/UserContext";
import { LogOut } from "lucide-react";
import { Plus, Users, Building2, Handshake, StickyNote, CheckSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { resolveLayoutGate } from "./layout-gate";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTerminology } from "@/contexts/TerminologyContext";
import { useAuth0 } from "@auth0/auth0-react";
import { eventBus, EventTypes } from "@/lib/custom-event-bus";
import { useFeatureFlag, FEATURE_FLAGS } from "@/lib/feature-flags";
import AskCoffeePanel from "@/components/shared/AskCoffeePanel";
import NewFeatureBanner from "@/components/shared/NewFeatureBanner";
import PastDuePaymentBanner from "@/components/layout/PastDuePaymentBanner";
import CreditBalanceBanner from "@/components/layout/CreditBalanceBanner";
import OverageAutoReload from "@/components/layout/OverageAutoReload";
import OverageGate from "@/components/layout/OverageGate";
import DisconnectedAccountBanner from "@/components/layout/DisconnectedAccountBanner";
import SuperuserFlagsBanner from "@/components/layout/SuperuserFlagsBanner";
import NotificationBell from "@/components/layout/NotificationBell";
import AddPersonDialog from "@/components/shared/AddPersonDialog";
import AddCompanyDialog from "@/components/shared/AddCompanyDialog";
import AddDealDialog from "@/components/shared/AddDealDialog";
import AddNoteDialog from "@/components/shared/AddNoteDialog";
import AddTaskDialog from "@/components/shared/AddTaskDialog";
import { toast } from "sonner";
import { useSubscriptionGuard } from "@/lib/hooks/use-subscription-guard";
import { useIdlePrefetch } from "@/lib/hooks/use-idle-prefetch";
import { useBilling } from "@/lib/hooks/use-billing";
import PersonDetailSheet from "@/components/shared/PersonDetailSheet";
import CompanyDetailSheet from "@/components/shared/CompanyDetailSheet";
import DealDetailSheet from "@/components/shared/DealDetailSheet";
import TaskDetailSheet from "@/components/shared/TaskDetailSheet";
import ComposeEmailDialog from "@/components/shared/ComposeEmailDialog";
import { people, companies, deals } from "@/data/dummy";
import { useGetContactLazyQuery } from "@/lib/graphql/types-and-hooks";
import { useEntitySheetParams } from "@/lib/hooks/use-entity-sheet-params";
import { usePreviewAddedDeals } from "@/contexts/PreviewAddedDealsContext";
import { isLovablePreview } from "@/lib/utils/environment";
import { format } from "date-fns";

type AddDialogType = "person" | "company" | "deal" | "note" | "task" | null;

// When a note/task add dialog is opened from an entity detail sheet, the sheet
// passes the parent entity so the dialog can pre-fill the association.
type AddDialogAssociation = { id: string; type: "person" | "company" | "deal"; name: string };

/**
 * Route → translation key for the browser tab.
 *
 * Holds KEYS, not copy: a module constant that called `t()` would capture the
 * startup language and never update, so the lookup happens in the effect below
 * and the tab re-titles itself when the user switches language.
 */
const PAGE_TITLE_KEYS: Record<string, string> = {
  '/': 'pageTitles.today',
  '/notes': 'pageTitles.notes',
  '/tasks': 'pageTitles.tasks',
  '/people': 'pageTitles.people',
  '/companies': 'pageTitles.companies',
  '/deals': 'pageTitles.deals',
  '/favorites': 'pageTitles.favorites',
  '/reports': 'pageTitles.reports',
  '/agents': 'pageTitles.agents',
  '/list-builder': 'pageTitles.listBuilder',
  '/settings': 'pageTitles.settings',
  '/help': 'pageTitles.help',
  '/invite': 'pageTitles.invite',
};

export default function AppLayout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { displayName, profileImageUrl, currentUser, loading: userLoading, refetch: refetchUser } = useUser();
  const { logout, isAuthenticated } = useAuth0();
  const { shouldRedirect: subscriptionRedirect } = useSubscriptionGuard();
  const { billingInfo } = useBilling();
  const { terminology, singular } = useTerminology();
  const showAddButton = useFeatureFlag(FEATURE_FLAGS.SIDEBAR_ADD_BUTTON);
  const { addDeal: addPreviewDeal } = usePreviewAddedDeals();

  // Warm grid caches + backend during idle time after startup. Self-gated:
  // preview-skipped, waits for workspace/user, min-delay + idle-callback
  // scheduled, sequential, once per workspace per session (module sentinel).
  useIdlePrefetch();

  // Set document title based on current route. "Coffee" is the brand and stays
  // English in every language; only the page half is translated. An unknown
  // route keeps its historical "Coffee — Coffee".
  useEffect(() => {
    const key = PAGE_TITLE_KEYS[location.pathname];
    document.title = `${key ? t(key) : 'Coffee'} — Coffee`;
  }, [location.pathname, t]);


  const showNotifications = useFeatureFlag(FEATURE_FLAGS.NOTIFICATIONS);
  const showAskCoffee = useFeatureFlag(FEATURE_FLAGS.ASK_COFFEE);
  const [askCoffeeOpen, setAskCoffeeOpen] = useState(false);
  const [askCoffeeInitialQuery, setAskCoffeeInitialQuery] = useState<string | undefined>();
  const [activeAddDialog, setActiveAddDialog] = useState<AddDialogType>(null);
  const [addDialogAssociation, setAddDialogAssociation] = useState<AddDialogAssociation | null>(null);
  const [addDealPrefill, setAddDealPrefill] = useState<{
    company?: string;
    name?: string;
    amount?: number;
    stage?: string;
    closeDate?: string;
    owner?: string;
  } | undefined>(undefined);
  // Tracks whether the currently-open Add dialog was launched by Ask Coffee, so
  // we can refresh list pages after creation to surface the new entity.
  const addFromAskCoffeeRef = useRef(false);


  // Ask Coffee (and other internal callers) can open the Add Deal dialog with
  // prefilled values via this window event.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as typeof addDealPrefill | undefined;
      setAddDealPrefill(detail);
      addFromAskCoffeeRef.current = true;
      setActiveAddDialog("deal");
    };
    window.addEventListener("askCoffeeOpenAddDeal", handler);
    return () => window.removeEventListener("askCoffeeOpenAddDeal", handler);
  }, []);

  // Email-draft action (still local — no URL representation)
  const [notifComposeRecipients, setNotifComposeRecipients] = useState<{ id: string; name: string; email: string; title?: string; company?: string }[] | null>(null);

  // Lazy, fired on click: this component is always mounted, so an eager query here
  // would run on every page load for a dialog almost nobody opens.
  const [fetchDraftContact] = useGetContactLazyQuery({ notifyOnNetworkStatusChange: false });

  /**
   * Open the compose dialog for a notification's "Open draft" CTA.
   *
   * The previous implementation resolved the recipient with `people.find(...)` against
   * `@/data/dummy` — a STATIC import, bundled into every build and not gated on preview.
   * In production that lookup SUCCEEDED against a fictional contact, opening a live,
   * send-capable dialog addressed to a made-up external address for any user. So this
   * resolves the real contact by id, and says so honestly when it cannot.
   */
  const openNotificationDraft = useCallback(async (contactId: string) => {
    if (isLovablePreview()) {
      const person = people.find(p => p.id === contactId);
      if (person) {
        setNotifComposeRecipients([{ id: person.id, name: person.name, email: person.email, title: person.title, company: person.company }]);
      } else {
        toast.error(t("notifications.error"));
      }
      return;
    }

    try {
      const { data } = await fetchDraftContact({ variables: { id: contactId } });
      const contact = data?.contact;
      const email = contact?.primaryEmail;
      if (!contact || !email) {
        // An honest failure. The old code's fallback toast claimed the draft had
        // opened when nothing had.
        toast.error(t("notifications.error"));
        return;
      }
      const name = [contact.properties?.firstName, contact.properties?.lastName]
        .filter(Boolean)
        .join(" ");
      setNotifComposeRecipients([{
        id: contact.id,
        name: name || email,
        email,
        title: contact.properties?.jobTitle ?? undefined,
        company: contact.accounts?.edges?.[0]?.node?.properties?.name ?? undefined,
      }]);
    } catch {
      toast.error(t("notifications.error"));
    }
  }, [fetchDraftContact, t]);

  // App-level overlay sheets driven by ?personSheet, ?companySheet, ?dealSheet.
  // Used by Favorites taps, AskCoffeePanel results, and notification actions so
  // any caller produces a shareable URL while keeping the originating page mounted.
  const personSheetId = searchParams.get('personSheet');
  const companySheetId = searchParams.get('companySheet');
  const dealSheetId = searchParams.get('dealSheet');
  // A real task, as opposed to the task SUGGESTIONS that render inside a meeting sheet.
  // Added for the assignedTask notification, whose CTA has to land on one specific task.
  const taskSheetId = searchParams.get('taskSheet');

  // Dedicated list pages render their own deep-linked sheet via /people/:id, /companies/:id, /deals/:id.
  // Suppress the overlay there to avoid double-rendering.
  const isOnPersonRoute = /^\/people\/[^/]+/.test(location.pathname);
  const isOnCompanyRoute = /^\/companies\/[^/]+/.test(location.pathname);
  const isOnDealRoute = /^\/deals\/[^/]+/.test(location.pathname);

  // Hydrate from dummy data in Lovable preview; production sheets fetch by id.
  const overlayPerson = personSheetId && !isOnPersonRoute ? (people.find(p => p.id === personSheetId) ?? { id: personSheetId }) : null;
  const overlayCompany = companySheetId && !isOnCompanyRoute ? (companies.find(c => c.id === companySheetId) ?? { id: companySheetId }) : null;
  const overlayDeal = dealSheetId && !isOnDealRoute ? (deals.find(d => d.id === dealSheetId) ?? { id: dealSheetId }) : null;

  const { openEntitySheet, closeEntitySheet } = useEntitySheetParams();
  const [showTour, setShowTour] = useState(false);

  // Check for product tour flag
  useEffect(() => {
    if (sessionStorage.getItem("coffee_product_tour") === "true") {
      sessionStorage.removeItem("coffee_product_tour");
      const timer = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  // Allow `?tour=1` in the URL to kick off the product tour. The onboarding
  // preview entry in the hidden UI menu uses this so the tour starts as soon
  // as the user lands on the onboarded workspace.
  useEffect(() => {
    if (searchParams.get("tour") === "1") {
      const timer = setTimeout(() => setShowTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const tourSteps = useMemo<TourStep[]>(() => ([
    {
      target: "[data-tour='meetings-section']",
      title: "Your Meetings",
      description: "Here are your upcoming meetings. You can select dates for future or past meetings using the date picker. Click the Record button to toggle meeting recording on and off, or click the Briefings button to see an AI-generated meeting prep document.",
      placement: "right",
      route: "/",
      beforeShow: () => window.dispatchEvent(new CustomEvent("tourSwitchTodayTab", { detail: "meetings" })),
    },
    {
      target: "[data-tour='today-tasks-section']",
      title: "Your Tasks",
      description: "View and manage your tasks for the day. You can mark tasks as complete, set priorities, and associate them with people, companies, or deals. Tasks are organized by due date so you always know what needs attention.",
      placement: "right",
      route: "/",
      beforeShow: () => {
        window.dispatchEvent(new CustomEvent("tourSwitchTodayTab", { detail: "tasks" }));
        window.dispatchEvent(new CustomEvent("tourSetTaskView", { detail: "upcoming" }));
      },
    },
    {
      target: "[data-tour='companies-table']",
      title: "Your Companies",
      description: "This is your companies list. Coffee automatically creates and enriches company records from your meetings and emails. You can filter, sort, and click into any company to see associated people, deals, and activity.",
      placement: "top",
      route: "/companies",
    },
    {
      target: "[data-tour='people-table']",
      title: "Your People",
      description: "Here are all your contacts. Coffee builds this list from your email and calendar activity, enriching each person with their title, company, and social profiles. Click any person to see their full history and associations.",
      placement: "top",
      route: "/people",
    },
    {
      target: "[data-tour='notes-table']",
      title: "Inside Meetings & Notes",
      description: "Every meeting on your calendar becomes a row here. Click into a Test Meeting to see Coffee's AI summary, transcript, attendee context, and follow-up email — all generated automatically after the call ends.",
      placement: "top",
      route: "/notes?onboarding=1",
    },
    {
      target: "[data-tour='deals-table']",
      title: "Inside a Deal",
      description: "Deals roll up everything happening with an opportunity — meetings, emails, contacts, notes, and AI-generated risk and next-step suggestions. Click a Test Deal to see the full drill-down with deal score, activity timeline, and stage history.",
      placement: "top",
      route: "/deals?onboarding=1",
    },
  ]), []);

  // Global Ctrl+K / Cmd+K shortcut
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault();
      setAskCoffeeOpen(prev => !prev);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Listen for custom event from sidebar nav
  useEffect(() => {
    const handler = () => {
      setAskCoffeeInitialQuery(undefined);
      setAskCoffeeOpen(true);
    };
    window.addEventListener("openAskCoffee", handler);
    return () => window.removeEventListener("openAskCoffee", handler);
  }, []);

  // Listen for custom event with a query (from AskBar, etc.)
  useEffect(() => {
    const handler = (e: Event) => {
      const query = (e as CustomEvent).detail as string;
      setAskCoffeeInitialQuery(query);
      setAskCoffeeOpen(true);
    };
    window.addEventListener("openAskCoffeeWithQuery", handler);
    return () => window.removeEventListener("openAskCoffeeWithQuery", handler);
  }, []);

  // Listen for add dialog events from sidebar
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      // Sidebar/page "+" buttons dispatch a bare type string; entity detail sheets
      // dispatch { type, association } so the note/task dialog can pre-fill the link.
      if (detail && typeof detail === "object") {
        setActiveAddDialog(detail.type as AddDialogType);
        setAddDialogAssociation(detail.association ?? null);
      } else {
        setActiveAddDialog(detail as AddDialogType);
        setAddDialogAssociation(null);
      }
    };
    window.addEventListener("openAddDialog", handler);
    return () => window.removeEventListener("openAddDialog", handler);
  }, []);

  // Pre-fill the note/task association when the add dialog was opened from an
  // entity detail sheet. AddNoteDialog expects NoteSubject-shaped objects;
  // AddTaskDialog expects the lighter AssocSubject shape.
  const addDialogNoteAssociations = useMemo(() => {
    if (!addDialogAssociation) return undefined;
    const { id, type, name } = addDialogAssociation;
    if (type === "company") return [{ __typename: "Account", id, properties: { name } }];
    if (type === "deal") return [{ __typename: "Deal", id, properties: { name } }];
    return [{ __typename: "Contact", id, properties: { firstName: name.split(" ")[0], lastName: name.split(" ").slice(1).join(" ") } }];
  }, [addDialogAssociation]);

  const addDialogTaskSubjects = useMemo(
    () => (addDialogAssociation ? [{ id: addDialogAssociation.id, type: addDialogAssociation.type, name: addDialogAssociation.name }] : undefined),
    [addDialogAssociation]
  );

  // Gate the app shell until user state is resolved — prevents both the flash
  // of app UI before the onboarding redirect and the blank page users saw
  // while the `me` queries were in flight (or had silently failed).
  const layoutGate = resolveLayoutGate({ loading: userLoading, isAuthenticated, currentUser });

  if (layoutGate === "loading") {
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (layoutGate === "error") {
    return (
      <div className="flex items-center justify-center h-screen w-screen bg-background">
        <div className="text-center">
          <p className="text-sm text-muted-foreground mb-4">{t("banners.accountLoadFailed")}</p>
          <Button variant="outline" size="sm" onClick={() => refetchUser()}>
            {t("banners.retry")}
          </Button>
        </div>
      </div>
    );
  }

  if (layoutGate === "onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  if (layoutGate === "choose-workspace") {
    return <Navigate to="/choose-workspace" replace />;
  }

  if (subscriptionRedirect) {
    return <Navigate to="/subscription-expired" replace />;
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background">
      <SuperuserFlagsBanner />
      <PastDuePaymentBanner />
      <CreditBalanceBanner />
      <OverageAutoReload />
      <OverageGate />
      <DisconnectedAccountBanner />
      <NewFeatureBanner
        id="brew-notes-august-2026"
        title={t("banners.brewNotesTitle")}
        description={t("banners.brewNotesDescription")}
        ctaLabel={t("banners.brewNotesCta")}
      />
      <div className="flex flex-1 overflow-hidden">
        <AppSidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <header className="flex items-center justify-end gap-2 px-6 h-[49px] border-b border-border bg-card shrink-0">
            {showAddButton && (
              <DropdownMenu>
                {/* data-testid is the E2E suite's auth canary: it is the one control
                    that proves the authenticated shell rendered, and its label is
                    translated, so tests must not match on the text. */}
                <DropdownMenuTrigger
                  data-testid="add-record-button"
                  className="mr-auto h-7 inline-flex items-center gap-1 rounded-md bg-accent text-accent-foreground hover:opacity-90 transition px-2"
                >
                  <span className="text-xs font-medium leading-none">{t("common.add", { defaultValue: "Add" })}</span>
                  <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="bottom">
                  <DropdownMenuItem onSelect={() => { setTimeout(() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: "person" })), 0); }} className="gap-2"><Users className="h-4 w-4" /> {singular.people}</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => { setTimeout(() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: "company" })), 0); }} className="gap-2"><Building2 className="h-4 w-4" /> {singular.companies}</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => { setTimeout(() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: "deal" })), 0); }} className="gap-2"><Handshake className="h-4 w-4" /> {singular.deals}</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => { setTimeout(() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: "note" })), 0); }} className="gap-2"><StickyNote className="h-4 w-4" /> {t("common.note")}</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => { setTimeout(() => window.dispatchEvent(new CustomEvent("openAddDialog", { detail: "task" })), 0); }} className="gap-2"><CheckSquare className="h-4 w-4" /> {t("common.task")}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {showNotifications && (
              <NotificationBell onComposeEmail={openNotificationDraft} />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-muted/50 transition outline-none">
                <UserAvatar
                  name={displayName}
                  src={profileImageUrl || undefined}
                  size="sm"
                  fixedColor="bg-orange-200 text-orange-800"
                />
                <span className="text-sm font-medium">{displayName}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="gap-2 text-destructive" onClick={() => { eventBus.emit(EventTypes.LOGOUT); logout({ logoutParams: { returnTo: window.location.origin + '/login' } }); }}>
                  <LogOut className="h-4 w-4" /> {t("common.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>
          {/*
            `key` on the routed content forces a remount when the language
            changes. Plenty of labels downstream are computed once and memoized
            — grid column definitions keyed on entity type, the always-mounted
            detail sheets — and a `t()` re-render does not reach them, so
            without this a switch leaves the page half-translated until a hard
            reload. This is a React remount only: Apollo's cache is untouched
            and remounted components read their queries straight back out of it
            (cache-first), so no refetch storm and no pagination reset.
          */}
          <main key={i18n.language} className="flex-1 overflow-y-auto">
            {children}
          </main>
        </div>
        {showAskCoffee && (
          <AskCoffeePanel
            open={askCoffeeOpen}
            onOpenChange={(open) => {
              setAskCoffeeOpen(open);
              if (!open) setAskCoffeeInitialQuery(undefined);
            }}
            initialQuery={askCoffeeOpen ? askCoffeeInitialQuery : undefined}
          />
        )}

        {/* Global Add Dialogs — opened from sidebar, stay on current page */}
        <AddPersonDialog
          open={activeAddDialog === "person"}
          onOpenChange={(open) => { if (!open) { setActiveAddDialog(null); addFromAskCoffeeRef.current = false; } }}
          onAdd={(person) => {
            toast.success(t("banners.recordAdded", { name: `${person.firstName} ${person.lastName}` }));
            const fromAC = addFromAskCoffeeRef.current;
            addFromAskCoffeeRef.current = false;
            setActiveAddDialog(null);
            if (fromAC && /^\/people(\/|$|\?)/.test(location.pathname)) {
              window.location.reload();
            } else {
              navigate("/people");
            }
          }}
        />
        <AddCompanyDialog
          open={activeAddDialog === "company"}
          onOpenChange={(open) => { if (!open) { setActiveAddDialog(null); addFromAskCoffeeRef.current = false; } }}
          onAdd={(company) => {
            toast.success(t("banners.recordAdded", { name: company.name }));
            const fromAC = addFromAskCoffeeRef.current;
            addFromAskCoffeeRef.current = false;
            setActiveAddDialog(null);
            if (fromAC && /^\/companies(\/|$|\?)/.test(location.pathname)) {
              window.location.reload();
            } else {
              navigate("/companies");
            }
          }}
        />
        <AddDealDialog
          open={activeAddDialog === "deal"}
          onOpenChange={(open) => {
            if (!open) {
              setActiveAddDialog(null);
              setAddDealPrefill(undefined);
              addFromAskCoffeeRef.current = false;
            }
          }}
          prefill={addDealPrefill}
          onAdd={(deal) => {
            toast.success(t("banners.recordAdded", { name: deal.name }));
            // In preview, persist the deal so it shows up when the Deals page
            // mounts (or is already mounted). Production saves via the
            // AddDealDialog's own createDeal mutation.
            if (isLovablePreview()) {
              addPreviewDeal({
                id: `added-${Date.now()}`,
                company: deal.company,
                name: deal.name,
                stage: deal.stage,
                amount: deal.amount,
                closeDate: deal.closeDate,
                contacts: "",
                owner: deal.owner,
                lastEmail: "-",
                lastMeeting: "-",
                nextMeeting: "-",
                billing: "-",
                lastStep: "-",
                nextStep: "-",
                createdDate: format(new Date(), "MMM d, yyyy"),
                createdBy: deal.owner,
                stageEnteredDate: format(new Date(), "MMM d, yyyy"),
              });
            }
            const fromAC = addFromAskCoffeeRef.current;
            addFromAskCoffeeRef.current = false;
            setActiveAddDialog(null);
            setAddDealPrefill(undefined);
            if (fromAC && /^\/deals(\/|$|\?)/.test(location.pathname)) {
              window.location.reload();
            } else {
              navigate("/deals");
            }
          }}
        />
        <AddNoteDialog
          open={activeAddDialog === "note"}
          initialAssociations={addDialogNoteAssociations as any}
          onOpenChange={(open) => {
            if (!open) {
              setActiveAddDialog(null);
              setAddDialogAssociation(null);
              const fromAC = addFromAskCoffeeRef.current;
              addFromAskCoffeeRef.current = false;
              if (fromAC && /^\/notes(\/|$|\?)/.test(location.pathname)) {
                window.location.reload();
              }
            }
          }}
        />
        <AddTaskDialog
          open={activeAddDialog === "task"}
          initialSubjects={addDialogTaskSubjects}
          onOpenChange={(open) => { if (!open) { setActiveAddDialog(null); setAddDialogAssociation(null); addFromAskCoffeeRef.current = false; } }}
          onAdd={(task) => {
            toast.success(t("banners.taskCreated", { title: task.title }));
            const fromAC = addFromAskCoffeeRef.current;
            addFromAskCoffeeRef.current = false;
            setActiveAddDialog(null);
            if (fromAC && /^\/tasks(\/|$|\?)/.test(location.pathname)) {
              window.location.reload();
            } else {
              navigate("/tasks");
            }
          }}
        />
        {showTour && (
          <ProductTour steps={tourSteps} onComplete={() => setShowTour(false)} />
        )}

        {/* App-level overlay detail sheets — driven by ?personSheet, ?companySheet, ?dealSheet
            so Favorites, AskCoffeePanel, and notification actions all produce shareable URLs */}
        {overlayPerson && (
          <PersonDetailSheet
            open
            onOpenChange={(open) => { if (!open) closeEntitySheet('personSheet'); }}
            person={overlayPerson as any}
            hideOverlay={askCoffeeOpen}
          />
        )}
        {overlayCompany && (
          <CompanyDetailSheet
            open
            onOpenChange={(open) => { if (!open) closeEntitySheet('companySheet'); }}
            company={overlayCompany as any}
            hideOverlay={askCoffeeOpen}
          />
        )}
        {overlayDeal && (
          <DealDetailSheet
            open
            onOpenChange={(open) => { if (!open) closeEntitySheet('dealSheet'); }}
            deal={overlayDeal as any}
            hideOverlay={askCoffeeOpen}
          />
        )}
        {taskSheetId && (
          // No dummy hydration: TaskDetailSheet takes an id and fetches, so unlike the
          // person/company/deal overlays there is nothing to pre-resolve.
          <TaskDetailSheet
            open
            taskId={taskSheetId}
            onOpenChange={(open) => { if (!open) closeEntitySheet('taskSheet'); }}
            hideOverlay={askCoffeeOpen}
          />
        )}
        {notifComposeRecipients && (
          <ComposeEmailDialog
            open={!!notifComposeRecipients}
            onOpenChange={(open) => { if (!open) setNotifComposeRecipients(null); }}
            recipients={notifComposeRecipients}
          />
        )}
      </div>
    </div>
  );
}

